import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check, Grid3X3, MapPin } from 'lucide-react';
import { GAMES, getGameConfig } from './leaderboardConfig';
import './GameSelector.css';

// Icon mapping - maps iconName from config to Lucide components
const ICON_MAP = {
  grid: Grid3X3,
  mapPin: MapPin,
};

// Helper to get icon component
const GameIcon = ({ iconName, size = 22, className = '' }) => {
  const IconComponent = ICON_MAP[iconName];
  if (!IconComponent) return null;
  return <IconComponent size={size} className={className} />;
};

/**
 * GameSelector - Dropdown component for selecting games in the leaderboard
 * Scalable to support 5+ games with icons, descriptions, and "coming soon" badges
 */
export default function GameSelector({ selectedGame, onGameChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  
  const selectedConfig = getGameConfig(selectedGame);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close dropdown on escape key
  useEffect(() => {
    function handleEscape(event) {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    }

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  const handleGameSelect = (game) => {
    if (game.enabled) {
      onGameChange(game.id);
      setIsOpen(false);
    }
  };

  return (
    <div className="game-selector" ref={dropdownRef}>
      
      <button 
        className={`game-selector-trigger ${isOpen ? 'open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <GameIcon iconName={selectedConfig?.iconName} className="game-icon" />
        <span className="game-name">{selectedConfig?.name}</span>
        <ChevronDown className={`chevron ${isOpen ? 'rotated' : ''}`} size={20} />
      </button>

      {isOpen && (
        <div className="game-dropdown" role="listbox">
          {GAMES.map((game) => (
            <button
              key={game.id}
              className={`game-option ${game.id === selectedGame ? 'active' : ''} ${!game.enabled ? 'disabled' : ''}`}
              onClick={() => handleGameSelect(game)}
              disabled={!game.enabled}
              role="option"
              aria-selected={game.id === selectedGame}
            >
              <GameIcon iconName={game.iconName} size={20} className="game-icon" />
              <span className="game-name">{game.name}</span>
              {!game.enabled && (
                <span className="coming-soon-badge">Coming Soon</span>
              )}
              {game.enabled && game.id === selectedGame && (
                <Check className="check-icon" size={18} />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

